import { observer } from "mobx-react-lite";
import React, { useEffect, useRef, useState } from "react";
import { Animated, Easing, StyleSheet } from "react-native";
import { Icon } from "~/components/Icon";
import { Pressable } from "~/components/Pressable";
import { Spacer } from "~/components/Spacer";
import { Text } from "~/components/Text";
import { View } from "~/components/View";
import { useStatic } from "~/hooks/useStatic";
import { styleConstants } from "~/style/styleConstants";
import { shadow } from "~/utils/shadow";
import type { ToastType } from "./ToastProvider";

export const ToastItem = observer(function ToastItem({
  toast,
  onRemove,
}: {
  toast: ToastType;
  onRemove: (id: string) => void;
}) {
  const isRemoving = useRef(false);

  useEffect(() => {
    setTimeout(() => {
      if (isRemoving.current) return;
      isRemoving.current = true;
      exitAnimation.start(() => {
        onRemove(toast.id);
      });
    }, 2500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const opacity = useStatic(() => new Animated.Value(0));

  const enterAnimation = useStatic(() =>
    Animated.timing(opacity, {
      toValue: 1,
      duration: 300,
      easing: Easing.linear,
      useNativeDriver: false,
    }),
  );

  useEffect(() => {
    enterAnimation.start();
    return () => {
      enterAnimation.stop();
    };
  }, [enterAnimation]);

  const exitAnimation = useStatic(() =>
    Animated.timing(opacity, {
      toValue: 0,
      duration: 300,
      easing: Easing.linear,
      useNativeDriver: false,
    }),
  );

  const [measuredHeight, setMeasuredHeight] = useState<undefined | number>(
    undefined,
  );

  return (
    <Animated.View
      pointerEvents="box-none"
      style={{
        opacity: measuredHeight === undefined ? 0 : opacity,
        overflow: "hidden",
        position: measuredHeight === undefined ? "absolute" : "relative",
        height:
          measuredHeight === undefined
            ? undefined
            : opacity.interpolate({
                inputRange: [0, 1],
                outputRange: [0, measuredHeight],
                easing: Easing.inOut(Easing.ease),
              }),
      }}
      onLayout={
        measuredHeight === undefined
          ? (event) => setMeasuredHeight(event.nativeEvent.layout.height)
          : undefined
      }
    >
      <View paddingVerticalSmall style={{ height: measuredHeight }}>
        <View
          style={[
            S.view,
            {
              backgroundColor:
                toast.type === "success"
                  ? styleConstants.colorBackgroundSuccess
                  : styleConstants.colorBackgroundDanger,
            },
          ]}
        >
          <Text colorLight style={S.flex}>
            {toast.message}
          </Text>
          <Spacer small />
          <>
            {typeof toast.rightText === "string" ? (
              <Text colorLight>{toast.rightText}</Text>
            ) : toast.rightText ? (
              toast.rightText
            ) : (
              <Pressable
                onPress={() => {
                  if (isRemoving.current) return;
                  isRemoving.current = true;
                  exitAnimation.start(() => {
                    onRemove(toast.id);
                  });
                }}
              >
                <Icon
                  name="close"
                  color={styleConstants.colorTextLight}
                  size={22}
                />
              </Pressable>
            )}
          </>
        </View>
      </View>
    </Animated.View>
  );
});

const S = StyleSheet.create({
  flex: { flex: 1 },
  view: {
    opacity: 1,
    padding: styleConstants.spacingMedium,
    borderRadius: 4,
    ...shadow(2),
    flexDirection: "row",
    alignItems: "center",
  },
});
